import React, { useContext } from 'react';
import { useInterpret } from '@xstate/react';
import { DataAccessContext } from '@chargify/data-access';
import { camelToUnderscore } from '@chargify/formatters';

import { ConfigContext, GlobalStateContext } from '../contexts';

import {
  billingHistoryData,
  initialBillingHistoryContext,
} from '../machines/billingHistoryData';

import { billingFilters } from '../machines/billingFilters';
import { filtersOptions } from '../machines/filtersOptions';
import { getDatesFilter } from '../utils/getDatesFilters';

type Props = {
  /**
   * React children nodes
   */
  children: React.ReactNode;
};

export const GlobalStateProvider = ({ children }: Props) => {
  const { dataAccess } = useContext(DataAccessContext);
  const { billingHistory } = useContext(ConfigContext);
  const { itemsPerPage } = billingHistory;
  const billingHistoryDataService = useInterpret(billingHistoryData, {
    context: {
      ...initialBillingHistoryContext,
      pagination: {
        ...initialBillingHistoryContext.pagination,
        itemsPerPage,
      },
    },
    services: {
      getInvoices: (ctx) => {
        const { sortBy, ...sortSettings } = ctx.sorting;
        return dataAccess.getInvoices({
          sortBy: camelToUnderscore(sortBy),
          ...ctx.filters,
          ...sortSettings,
        });
      },
    },
  });

  const billingFiltersService = useInterpret(billingFilters, {
    actions: {
      applyFilters: (ctx) => {
        const { date, ...filters } = ctx.activeFilters;
        const datesFilters = getDatesFilter(date);

        billingHistoryDataService.send({
          type: 'CHANGE_FILTERS',
          filters: {
            ...filters,
            ...datesFilters,
          },
        });
      },
      clearFilters: () => {
        billingHistoryDataService.send({
          type: 'CLEAR_FILTERS',
        });
      },
    },
  });

  const filtersOptionsService = useInterpret(filtersOptions, {
    services: {
      getFiltersOptions: () => dataAccess.getInvoiceFilters(),
    },
  });

  return (
    <GlobalStateContext.Provider
      value={{
        billingHistoryDataService,
        billingFiltersService,
        filtersOptionsService,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};
